import { round } from 'lodash'

type Rect = {
  x: number
  y: number
  w: number
  h: number
}

export function rectToViewportRect(viewport: any, rectPdf: Rect): Rect {
  const [x1, y1] = viewport.convertToViewportPoint(rectPdf.x, rectPdf.y)
  const [x2, y2] = viewport.convertToViewportPoint(
    rectPdf.x + rectPdf.w,
    rectPdf.y + rectPdf.h
  )

  const left = Math.min(x1, x2)
  const top = Math.min(y1, y2)
  const right = Math.max(x1, x2)
  const bottom = Math.max(y1, y2)

  return {
    x: round(left, 2),
    y: round(top, 2),
    w: round(right - left, 2),
    h: round(bottom - top, 2),
  }
}

export function rectToPdfRect(viewport: any, rectVp: Rect): Rect {
  const [x1, y1] = viewport.convertToPdfPoint(rectVp.x, rectVp.y)
  const [x2, y2] = viewport.convertToPdfPoint(
    rectVp.x + rectVp.w,
    rectVp.y + rectVp.h
  )

  const left = Math.min(x1, x2)
  const top = Math.min(y1, y2)
  const right = Math.max(x1, x2)
  const bottom = Math.max(y1, y2)

  return {
    x: round(left, 2),
    y: round(top, 2),
    w: round(right - left, 2),
    h: round(bottom - top, 2),
  }
}

export function pointInRect(px: number, py: number, rect: Rect): boolean {
  return (
    px >= rect.x &&
    px <= rect.x + rect.w &&
    py >= rect.y &&
    py <= rect.y + rect.h
  )
}

export function computeNearestWordInThreshold(
  px: number,
  py: number,
  words: Array<{ rectVp: Rect }>,
  thresholdPx: number
): number | null {
  let nearestIdx: number | null = null
  let bestDist2 = Infinity
  const threshold2 = thresholdPx * thresholdPx

  for (let i = 0; i < words.length; i++) {
    const r = words[i].rectVp
    const cx = r.x + r.w / 2
    const cy = r.y + r.h / 2

    const dx = px - cx
    const dy = py - cy
    const dist2 = dx * dx + dy * dy

    if (dist2 <= threshold2 && dist2 < bestDist2) {
      bestDist2 = dist2
      nearestIdx = i
    }
  }

  return nearestIdx
}

export function extractPrefixSuffix(
  globalText: string,
  startOffset: number,
  endOffset: number,
  windowSize: number
): { prefix: string; suffix: string } {
  const prefixStart = Math.max(0, startOffset - windowSize)
  const prefix = globalText.slice(prefixStart, startOffset)

  const suffixEnd = Math.min(globalText.length, endOffset + windowSize)
  const suffix = globalText.slice(endOffset, suffixEnd)

  return { prefix, suffix }
}

export function getAnchorClientRect(
  viewport: any,
  wrapperEl: HTMLElement | null,
  anchorRect: {
    leftOffset: number
    topOffset: number
    width: number
    height: number
  }
): DOMRect | null {
  if (!wrapperEl) return null

  const rectPdf = {
    x: anchorRect.leftOffset,
    y: anchorRect.topOffset,
    w: anchorRect.width,
    h: anchorRect.height,
  }

  const vpRect = rectToViewportRect(viewport, rectPdf)
  const wrapperRect = wrapperEl.getBoundingClientRect()

  const clientX = wrapperRect.left + vpRect.x
  const clientY = wrapperRect.top + vpRect.y

  return new DOMRect(clientX, clientY, vpRect.w, vpRect.h)
}

export function getAnchorPageRect(
  viewport: any,
  anchorRect: {
    leftOffset: number
    topOffset: number
    width: number
    height: number
  }
): DOMRect {
  const rectPdf = {
    x: anchorRect.leftOffset,
    y: anchorRect.topOffset,
    w: anchorRect.width,
    h: anchorRect.height,
  }

  const vpRect = rectToViewportRect(viewport, rectPdf)

  // IMPORTANT: this is page-local (0,0 is the wrapper top-left)
  return new DOMRect(vpRect.x, vpRect.y, vpRect.w, vpRect.h)
}
